/**
 * LABOUR STATE -- the one place the four-state vocabulary is read.
 *
 * > Pete, 2026-09-24: *"Paused on the kiosk, not anywhere esle."*
 *
 * ---- ⛔⛔ WHAT WENT WRONG, SO IT CANNOT GO WRONG THE SAME WAY -------------
 *
 * Migration 201 split "the assignment is open" from "a clock is running", and
 * `dbo.vw_LaborAssignmentAll.State` has answered `Active | OnBreak | Paused |
 * Finished` since 2026-09-14. The Dock BACKEND shipped that field the same day.
 * The Dock FRONTEND never read it -- `LabourSessionDto` in `api/client.ts` and
 * `BoardLaborDto` in `api/types.ts` are HAND-MAINTAINED MIRRORS of the server
 * DTOs, and they simply did not declare the field.
 *
 *   ** A HAND-MAINTAINED TYPE MIRROR IS A SECOND SOURCE OF TRUTH, AND THE HALF
 *      THAT LAGS IS INVISIBLE: THE COMPILER REPORTS THE MISSING FIELD EXACTLY AS
 *      THOUGH THE SERVER HAD NEVER SENT IT. **
 *
 * So for ten days the Labor rail drew "In progress", emerald, with a spinning
 * truck, over a task nobody was touching -- and `labourRepository.ts` carried a
 * comment saying it would, waiting for a reader that never arrived.
 *
 * ---- ⭐ WHY THIS FILE AND NOT A HELPER IN EACH COMPONENT -----------------
 *
 * Four surfaces ask the same three questions: the Labor rail, the floor board,
 * the Gantt/week/month chip, and the check-in panel.
 *   ** WHEN FOUR PLACES MUST DO THE SAME LAST STEP, THE STEP BELONGS TO WHAT
 *      CALLS THEM. **
 */

/**
 * ⭐ THE VIEW'S OWN WORD, not a re-derivation. `dbo.vw_LaborAssignmentAll.State`.
 * ⚠️ `undefined` MEANS NOT ASKED FOR -- the board reader's degradation ladder
 * does not select it on its lower rungs. ⛔ It does NOT mean Paused, and a caller
 * that treats it so turns a missing column grant into "nobody is working".
 */
export type AssignmentState = 'Active' | 'OnBreak' | 'Paused' | 'Finished';

/** How a surface should draw a row or a card. */
export type LabourTone = 'working' | 'paused' | 'done';

export interface LabourLook {
    tone: LabourTone;
    /** The word. ⚠️ Never a bare count or a colour alone -- §1AV.253. */
    label: string;
    /** Hover text / title. Longer, because it has room. */
    title: string;
}

/**
 * ⭐⭐ ONE FUNCTION, FOUR SURFACES.
 *
 * ⛔ `finished` IS PASSED SEPARATELY AND LEADS. A closed assignment is finished
 * whatever its intervals say -- the same precedence the SQL view uses, written
 * the same way round, because the two disagreeing is exactly the class of bug
 * this file exists to end.
 *
 * ⚠️ AND THE FALLBACK WHEN `assignmentState` IS ABSENT IS THE OLD TWO-STATE
 * ANSWER, never 'paused'. An older backend, or a board reader that fell to a
 * lower rung, must degrade to what the product did yesterday.
 *   ** A NEW FIELD'S ABSENCE MUST MEAN "I DO NOT KNOW", AND "I DO NOT KNOW"
 *      MUST NEVER RENDER AS A CONFIDENT NEW ANSWER. **
 */
export function labourLook(
    assignmentState: AssignmentState | null | undefined,
    breakKind: string | null | undefined,
    finished: boolean,
): LabourLook {
    if (finished) {
        return { tone: 'done', label: 'Finished', title: 'Work finished on this appointment' };
    }
    switch (assignmentState) {
        case 'Paused':
            return {
                tone: 'paused',
                label: 'PAUSED',
                title: 'Paused - the assignment is open and nobody is working it',
            };
        case 'OnBreak':
            /**
             * ⭐ THE KIND IS NAMED, and Pete chose that deliberately: *"Four
             * states, break kind named."* "He is at lunch" and "he left this
             * truck" are different sentences to a supervisor -- one man is back
             * in ten minutes, the other has moved on.
             * ⚠️ `breakKind` is 'Break' or 'Meal' from the view; anything else
             * falls back to the generic word rather than printing a raw code.
             */
            return breakKind === 'Meal'
                ? { tone: 'paused', label: 'ON MEAL', title: 'On a meal break' }
                : { tone: 'paused', label: 'ON BREAK', title: 'On a break' };
        case 'Finished':
            return { tone: 'done', label: 'Finished', title: 'Work finished on this appointment' };
        case 'Active':
            return { tone: 'working', label: 'In progress', title: 'Being worked - loading/unloading in progress' };
        default:
            /* ⚠️ NOT ASKED FOR. See the header on this function. */
            return { tone: 'working', label: 'In progress', title: 'Being worked - loading/unloading in progress' };
    }
}

/**
 * ⭐⭐ WHEN THE CLOCK STOPPED -- and ONLY when it is actually stopped.
 *
 * ⛔ `lastIntervalEndedUtc` (migration 217) IS RAW BY DESIGN: an Active
 * assignment has one too (the end of the interval before the running one), and
 * so does a finished one. Pairing it with the state is this function's whole
 * job, and it happens HERE so the "is this paused" test lives in one place --
 * the view's `State` column -- rather than in four components that can drift.
 *   ** A COLUMN WHOSE MEANING DEPENDS ON ANOTHER COLUMN'S VALUE IS TWO COLUMNS
 *      PRETENDING TO BE ONE, UNLESS EXACTLY ONE FUNCTION JOINS THEM. **
 */
export function pausedSince(
    assignmentState: AssignmentState | null | undefined,
    lastIntervalEndedUtc: string | null | undefined,
): string | null {
    if (assignmentState !== 'Paused' && assignmentState !== 'OnBreak') return null;
    return lastIntervalEndedUtc ?? null;
}

/**
 * ⭐ WORKED time, in the form a person reads: "47m", "2h 05m".
 *
 * > Pete, 2026-09-24, choosing this over wall-clock: *"Worked time, with a
 * > paused-since line."*
 *
 * ⛔ THIS IS NOT TIME-SINCE-STARTED. `WorkedSeconds` sums WORK intervals only,
 * so a task paused overnight and resumed at 06:00 reports the hours somebody
 * actually stood on it -- which is the number job costing needs and the number
 * the old row was quietly overstating.
 *
 * ⚠️ RETURNS null RATHER THAN "0m" WHEN THE FIELD IS ABSENT. `undefined` means
 * the board reader fell to a rung that does not select it, and "0m worked" is a
 * measurement where there was none.
 *   ** A NULL IS AN HONEST GAP. A ZERO IS A CLAIM. **
 */
export function workedLabel(workedSeconds: number | null | undefined): string | null {
    if (workedSeconds === null || workedSeconds === undefined) return null;
    const mins = Math.max(0, Math.round(workedSeconds / 60));
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
}

/**
 * ⭐ "12m ago" / "1h 20m ago" -- how long a thing has been true.
 * ⚠️ ELAPSED, NEVER A CLOCK TIME. A wall display may be in a different timezone
 * from the server and its own clock may be wrong; "12m ago" is true regardless,
 * and a wrong wall-clock time would be believed. (The kiosk's `openFor` carries
 * this same note, and for the same reason.)
 */
/**
 * ⭐⭐ THE WORD FOR WHAT IS HAPPENING TO THE LOAD (2026-09-24).
 *
 * > Pete: *"Can we get the unloaded/loaded symbol/text somewhere on the appointment
 * > because it stays arrived but needs a status of that process."* -> *"the glyph on
 * > the appointment card because its small but a status on the hover over
 * > information at the top (loaded, Unloaded, Loading Unloading)."*
 *
 * ⛔ THE APPOINTMENT STATUS IS NOT WRONG AND IS NOT CHANGED. A truck stays ARRIVED
 * until it DEPARTS -- that is what the status means. This is a different question
 * running alongside it, and it gets its own field rather than bending the status.
 *   ** TWO QUESTIONS ABOUT ONE ROW NEED TWO FIELDS. FOLDING THE SECOND INTO THE
 *      FIRST IS HOW A STATUS COLUMN STOPS MEANING WHAT ITS NAME SAYS. **
 *
 * ⚠️ DIRECTION DECIDES THE VERB. `OUTBOUND` is a LOAD, `INBOUND` is an UNLOAD --
 * the same mapping the board's DTO makes when it calls them 'load' and 'unload'.
 * ⚠️ `BLOCK` HAS NO LOAD, so it returns null rather than inventing one.
 *
 * ⛔ RETURNS null WHEN NOBODY HAS TOUCHED IT. A truck that has arrived and not been
 * started has no work status, and "not started" is the ABSENCE of this answer, not a
 * fifth value of it. ** A VOCABULARY THAT INCLUDES "NOTHING YET" WILL BE PRINTED. **
 */
export function workStatusLabel(
    direction: string | null | undefined,
    laborActive: boolean | undefined,
    laborPaused: boolean | undefined,
    laborDone: boolean | undefined,
): string | null {
    /* ⛔⚤ ~~`if (direction === 'BLOCK') return null; const load = direction === 'OUTBOUND';`~~
       — 2026-09-24, when the filter below needed the same rule. ⚠️ AND IT TIGHTENS ONE
       CASE: the old form made EVERY non-OUTBOUND, non-BLOCK value "Unloading", so a
       direction this file did not recognise was asserted to be an inbound. `Direction`
       is exhaustively `INBOUND | OUTBOUND | BLOCK`, so no real row changes — but the
       parameter is typed `string | null | undefined`, and the honest answer to a value
       nobody planned for is no answer.
         ** A FALLBACK THAT PICKS ONE OF TWO REAL ANSWERS FOR AN UNKNOWN INPUT IS RIGHT
            HALF THE TIME AND INDISTINGUISHABLE FROM KNOWING. ** */
    const verb = workVerb(direction);
    if (verb === null) return null;
    const load = verb === 'LOADING';
    /* ⭐ SAME PRECEDENCE AS EVERYWHERE ELSE: somebody on it beats paused beats done.
       The server already ranked them; this must not disagree. */
    if (laborActive) return load ? 'Loading' : 'Unloading';
    if (laborPaused) return load ? 'Loading (paused)' : 'Unloading (paused)';
    if (laborDone)   return load ? 'Loaded'  : 'Unloaded';
    return null;
}

/**
 * ============================================================================
 * ⭐⭐⭐ LOADING / UNLOADING AS A **FILTER AXIS** (Pete, 2026-09-24)
 * ============================================================================
 *
 * > *"it would be great to filter the calendar by Unloading and Loading in the
 * > status bar, is it possible to add those?"* → *"Separate control beside the
 * > status bar."*
 *
 * ---- ⛔⛔ WHY IT IS NOT A STATUS, AND WHY THAT IS NOT A TECHNICALITY -------
 *
 * `statusCode` stays `ARRIVED` for the whole visit — that is what the status
 * MEANS, and `workStatusLabel`'s header says so in Pete's own words: *"it stays
 * arrived but needs a status of that process."* So Loading and Unloading cannot
 * be eighth and ninth entries in `APPOINTMENT_STATUSES`; they are a SECOND
 * QUESTION about the same row, and a second question is a second axis.
 *   ** TWO QUESTIONS ABOUT ONE ROW NEED TWO FIELDS. A FILTER OVER THE SECOND
 *      THAT PRETENDS TO BE A VALUE OF THE FIRST MAKES BOTH UNSELECTABLE
 *      TOGETHER — "arrived AND being unloaded" WOULD BE UNASKABLE. **
 * ⭐ So the control is separate and the two filters **AND**. Pete picked that
 * shape when offered it, and it is also the only shape that can answer the
 * question a scheduler actually has at 10am.
 *
 * ---- ⭐ THE DIRECTION RULE LIVES IN ONE FUNCTION -------------------------
 *
 * `workVerb` is the whole of "OUTBOUND is a load, INBOUND is an unload, a BLOCK
 * is neither". ⛔ `workStatusLabel` above CALLS it rather than restating it.
 *   ** A RULE WRITTEN TWICE IS TWO RULES, AND THE SECOND ONE IS THE ONE NOBODY
 *      UPDATES. **
 */
export type WorkKindCode = 'LOADING' | 'UNLOADING';

/** ⛔ The list, as a value, so the chips cannot be a second hand-typed copy. */
export const WORK_KINDS: readonly { code: WorkKindCode; label: string }[] = [
    { code: 'LOADING',   label: 'Loading' },
    { code: 'UNLOADING', label: 'Unloading' },
] as const;

/** ⚠️ `null` for a BLOCK (a door outage is not a load) and for an unknown direction. */
export function workVerb(direction: string | null | undefined): WorkKindCode | null {
    if (direction === 'BLOCK') return null;
    if (direction === 'OUTBOUND') return 'LOADING';
    if (direction === 'INBOUND')  return 'UNLOADING';
    return null;
}

/**
 * ⭐⭐ WHICH CHIP, IF ANY, THIS LOAD BELONGS UNDER.
 *
 * ⛔⛔ IN PROGRESS MEANS **ACTIVE OR PAUSED**, AND "PAUSED" IS NOT A THIRD CHIP.
 * A truck whose loader stepped off for a meal is still a truck in the middle of
 * being loaded — `workStatusLabel` says so itself, returning *"Loading (paused)"*
 * rather than dropping the verb. Excluding it would make the chip mean "somebody
 * is standing there this second", which is a staffing question, not a load one.
 *   ** THE FILTER MUST SELECT WHAT THE WORD ON THE TILE SAYS. IF THE TILE READS
 *      "LOADING (PAUSED)" AND THE LOADING CHIP HIDES IT, THE SCREEN IS ARGUING
 *      WITH ITSELF. **
 *
 * ⛔ AND `laborDone` IS **NOT** INCLUDED. "Loaded" is the finished state; a chip
 * labelled *Loading* that returned finished trucks would be the one thing worse
 * than a missing filter — a filter that quietly answers a different question.
 * ⬜ If Pete wants "Loaded / Unloaded" later, they are two MORE chips here, not a
 * widening of these two.
 *
 * ⚠️ `laborPaused` IS OPTIONAL ON THE DTO and is tested `=== true`. `undefined`
 * means a degraded payload did not say, and "I do not know" must never render as
 * a confident new answer.
 */
export function workKind(
    direction: string | null | undefined,
    laborActive: boolean | undefined,
    laborPaused: boolean | undefined,
): WorkKindCode | null {
    if (!laborActive && laborPaused !== true) return null;
    return workVerb(direction);
}

export function agoLabel(iso: string | null | undefined, nowMs: number): string | null {
    if (!iso) return null;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return null;
    const mins = Math.max(0, Math.round((nowMs - t) / 60000));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
}
